import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQPage = () => {
  const { t } = useLanguage();
  const [faqItems, setFaqItems] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/faq`).then(res => {
      setFaqItems(res.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen pt-24" data-testid="faq-page">
      <div className="container-custom py-12 max-w-3xl">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8720C]/10 border border-[#E8720C]/20 mb-6">
            <HelpCircle className="w-4 h-4 text-[#E8720C]" />
            <span className="text-xs font-bold uppercase tracking-wider text-[#E8720C]">{t('faqPage.overline')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="faq-title">
            {t('faqPage.title')}
          </h1>
          <p className="text-base md:text-lg text-white/50 max-w-xl mx-auto">{t('faqPage.subtitle')}</p>
        </div>

        {/* FAQ Accordion */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#E8720C] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : faqItems.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <HelpCircle className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">{t('faqPage.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="faq-list">
            {faqItems.map((item, i) => (
              <div
                key={item.id || i}
                className={`glass-card overflow-hidden transition-all duration-300 ${openIndex === i ? 'border border-[#E8720C]/30' : 'border border-transparent hover:border-white/10'}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between p-5 text-left group"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-semibold text-sm md:text-base pr-4 group-hover:text-[#E8720C] transition-colors">
                    {item.question}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[#E8720C] shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`transition-all duration-300 ease-in-out ${openIndex === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <div className="px-5 pb-5 pt-0">
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-sm text-white/60 leading-relaxed" data-testid={`faq-answer-${i}`}>{item.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 glass-card p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/5 via-transparent to-transparent" />
          <div className="relative">
            <h3 className="text-xl font-bold mb-2">{t('faqPage.ctaTitle')}</h3>
            <p className="text-sm text-white/50 mb-4">{t('faqPage.ctaDesc')}</p>
            <a href="mailto:support@ogmusicgroup.com" className="btn-gold inline-flex" data-testid="faq-contact-btn">
              {t('faqPage.ctaButton')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
